import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { getLogger } from '@packages/core/logging';
import { HideFromTechShare, WaitForAssetProcessing } from '@packages/domain/techline/assets';
import {
    type AnswerAssetEntity,
    buildAnswerAssetBodies,
    CreateAndUploadAnswerAssets,
    type CreateAndUploadAnswerAssetsParams,
    DeleteDraftSubmission,
    FetchAllSubmissions,
    FetchSubmission,
    isValidWorksheetAnswer,
    RefreshSubmissions,
    SubmitAnswers,
    type SubmitAnswersBody,
    type SubmitAnswersResult,
    type WorksheetSubmissionEntity,
    type WorksheetSubmissionGroupEntity,
} from '@packages/domain/techline/worksheet-submissions';
import { FetchForm, type WorksheetFormEntity } from '@packages/domain/worksheets/forms';
import { FetchTitles, type FetchTitlesParams, type WorksheetCaseGroupEntity } from '@packages/domain/worksheets/groups';
import {
    AssetProcessingStateId,
    type WorksheetAnswerStatus,
    type WorksheetElementAnswers,
} from '@packages/models/api/worksheets';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { container } from '@web/services/container';
import { googleAnalyticsService, techlineService } from '@web/services/singletons';
import { cloneDeep, flatMap } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WorksheetGroup } from '../worksheet-group-accordion/worksheet-group-accordion.types';

const logger = getLogger('techline');

// Operations are prefixed with $ to make them visually distinct in this file.
const [
    $createAndUploadAnswerAssets,
    $deleteDraftWorksheetSubmission,
    $fetchAllWorksheetSubmissions,
    $fetchWorksheetForm,
    $fetchWorksheetSubmission,
    $fetchWorksheetTitles,
    $hideFromTechShare,
    $refreshWorksheetSubmissions,
    $submitWorksheetAnswers,
    $waitForAssetProcessing,
] = container.resolveOperations(
    CreateAndUploadAnswerAssets,
    DeleteDraftSubmission,
    FetchAllSubmissions,
    FetchForm,
    FetchSubmission,
    FetchTitles,
    HideFromTechShare,
    RefreshSubmissions,
    SubmitAnswers,
    WaitForAssetProcessing
);

/** Poll interval in milliseconds between worksheet answer asset completion checks. */
const WORKSHEET_ASSET_POLL_INTERVAL_MS = 5000;

/** Maximum number of poll iterations before giving up on worksheet answer assets. */
const WORKSHEET_ASSET_MAX_POLL_RETRIES = 5;

/** Parameters for the useWorksheetSubmissions hook. */
export type UseWorksheetSubmissionsParams = {
    /** The case number to manage worksheet submissions for. */
    caseNumber: string | undefined;

    /** Callback to notify the parent when worksheetGroupData changes. */
    onWorksheetGroupDataChange: (groups: TechlineCaseEntity['WorksheetSubmissionGroups'] | null) => void;

    /**
     * Worksheet submission groups from the case details. Provides
     * submission IDs and statuses needed to bulk-fetch and enrich
     * the available worksheet groups.
     */
    worksheetGroupData: WorksheetSubmissionGroupEntity[] | null | undefined;
};

/** Submission metadata used to enrich available worksheet groups with submission state. */
type SubmissionMeta = {
    /** ISO timestamp of the submission's last update. */
    lastUpdatedTimestamp: string;

    /** The submission status (e.g. "DRAFT", "SUBMITTED"). */
    status: string;

    /** The unique identifier for the worksheet submission. */
    submissionId: string;
};

/**
 * Manage worksheet submissions for a techline case: fetching forms,
 * fetching submissions, submitting answers, uploading answer assets,
 * deleting drafts, polling worksheet-level assets, and refreshing
 * worksheet group data. All API interactions are delegated to use cases;
 * this hook owns only React state and lifecycle.
 *
 * @param params - the case context and parent callback for worksheet operations
 * @returns worksheet submission state and mutation functions
 */
export function useWorksheetSubmissions(params: UseWorksheetSubmissionsParams) {
    const { caseNumber, onWorksheetGroupDataChange, worksheetGroupData } = params;

    // State
    const [isLoadingWorksheets, setIsLoadingWorksheets] = useState(false);
    const [worksheetFormData, setWorksheetFormData] = useState<WorksheetFormEntity | undefined>();
    const [worksheetSubmissionMap, setWorksheetSubmissionMap] = useState<Map<string, WorksheetFormEntity>>(new Map());
    const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
    const [worksheetSubmissionFetching, setWorksheetSubmissionFetching] = useState(false);
    const [worksheetTitleData, setWorksheetTitleData] = useState<WorksheetCaseGroupEntity[] | undefined>();
    const [availableWorksheetGroupData, setAvailableWorksheetGroupData] = useState<WorksheetGroup[]>([]);
    const [isLoadingAvailableGroups, setIsLoadingAvailableGroups] = useState(false);
    const [availableGroupError, setAvailableGroupError] = useState('');
    const worksheetAssetPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bulkFetchedSubmissionIdsRef = useRef<Set<string>>(new Set());
    const [hasLoadedInitialSubmissions, setHasLoadedInitialSubmissions] = useState(false);

    // Derived state

    /**
     * Enriches available worksheet groups with submission metadata (submissionId, status) from
     * the case's WorksheetSubmissionGroups, and with validation results from the in-memory
     * submission map. Each worksheet that has a draft submission gets an `isValid` flag derived
     * from `isValidWorksheetAnswer`.
     *
     * A single worksheet definition may have N submissions on the case, so catalog entries with
     * multiple submissions expand into N rows (one per submission). Catalog entries with zero
     * submissions remain as a single catalog-only row so the draft stepper can still offer them
     * for the first time (TECHSHARE-6411).
     */
    const enrichedWorksheetGroupData: WorksheetGroup[] = useMemo(() => {
        if (!worksheetGroupData?.length) {
            return availableWorksheetGroupData;
        }

        // Build a lookup: worksheetId -> every submission on the case for that worksheet
        const submissionsByWorksheetId = new Map<string, SubmissionMeta[]>();
        for (const group of worksheetGroupData) {
            for (const title of group.worksheetSubmissionTitles) {
                const meta: SubmissionMeta = {
                    lastUpdatedTimestamp: title.lastUpdatedTimestamp,
                    status: title.status,
                    submissionId: title.submissionId,
                };
                const existing = submissionsByWorksheetId.get(title.worksheetId);
                if (existing) {
                    existing.push(meta);
                } else {
                    submissionsByWorksheetId.set(title.worksheetId, [meta]);
                }
            }
        }

        return availableWorksheetGroupData.map((group) => ({
            ...group,
            worksheetTitles: group.worksheetTitles.flatMap((worksheet) => {
                const submissions = submissionsByWorksheetId.get(worksheet.worksheetId);
                if (!submissions?.length) {
                    return [worksheet];
                }

                // Emit one row per submission, each carrying its own validation verdict
                const hasMultipleSubmissions = submissions.length >= 2;
                return submissions.map((submission) => {
                    const submissionFormData = worksheetSubmissionMap.get(submission.submissionId);
                    const isValid =
                        submissionFormData && submission.status === 'DRAFT'
                            ? isValidWorksheetAnswer(submissionFormData)
                            : undefined;
                    return { ...worksheet, ...submission, hasMultipleSubmissions, isValid };
                });
            }),
        }));
    }, [availableWorksheetGroupData, worksheetGroupData, worksheetSubmissionMap]);

    /**
     * Validation results derived from the enriched worksheet group data. Keyed by submissionId so
     * multiple DRAFT instances of the same worksheet can each carry their own pass/fail verdict
     * (TECHSHARE-6411). Rows without a submissionId (pre-create catalog entries) are skipped.
     */
    const worksheetValidationResults: Record<string, boolean> = useMemo(() => {
        const results: Record<string, boolean> = {};

        for (const group of enrichedWorksheetGroupData) {
            for (const worksheet of group.worksheetTitles) {
                if (worksheet.isValid !== undefined && worksheet.submissionId) {
                    results[worksheet.submissionId] = worksheet.isValid;
                }
            }
        }

        return results;
    }, [enrichedWorksheetGroupData]);

    /**
     * The active worksheet submission data for the currently open editor.
     * Derived from the submission map using the last-requested submission ID,
     * or falls back to the worksheet form data for new submissions.
     */
    const [activeSubmissionId, setActiveSubmissionId] = useState<string | null>(null);

    /** The worksheet submission data for the currently open editor. */
    const worksheetSubmissionData: WorksheetFormEntity | undefined = useMemo(() => {
        if (activeSubmissionId) {
            return worksheetSubmissionMap.get(activeSubmissionId);
        }
        return worksheetFormData;
    }, [activeSubmissionId, worksheetFormData, worksheetSubmissionMap]);

    /**
     * Poll worksheet answer assets that are still uploading. Updates
     * the submission map entry in place when assets finish processing.
     * Stops after max retries.
     *
     * @param submissionId - the submission ID whose assets are being polled
     * @param uploadingAssets - assets still in Uploading state
     * @param retryCount - current retry count
     */
    const pollWorksheetAnswerAssets = useCallback(
        (
            submissionId: string,
            uploadingAssets: {
                /** The unique identifier for the asset being polled. */
                assetId: string;
            }[],
            retryCount: number
        ) => {
            if (!uploadingAssets.length) {
                return;
            }

            worksheetAssetPollRef.current = setTimeout(async () => {
                // Check status of each uploading asset
                try {
                    const responses = await Promise.all(
                        uploadingAssets.map((a) => techlineService.retrieveAsset(a.assetId))
                    );

                    const settledAssets: {
                        /** The unique identifier for the settled asset. */
                        assetId: string;

                        /** Additional asset properties returned by the API. */
                        [key: string]: unknown;
                    }[] = [];
                    const stillUploading: {
                        /** The unique identifier for the asset still uploading. */
                        assetId: string;
                    }[] = [];

                    responses.forEach((r) => {
                        if (r.success) {
                            const asset = r.data?.results?.asset;
                            if (asset?.assetProcessingStateId === AssetProcessingStateId.UPLOADING) {
                                stillUploading.push(asset);
                            } else if (asset) {
                                settledAssets.push(asset);
                            }
                        }
                    });

                    // Update settled assets in the submission map
                    if (settledAssets.length) {
                        setWorksheetSubmissionMap((prev) => {
                            const existing = prev.get(submissionId);
                            if (!existing) {
                                return prev;
                            }
                            const updated = cloneDeep(existing);
                            settledAssets.forEach((settled) => {
                                updated.elements.forEach((ele) => {
                                    if (ele.answerAssets?.length) {
                                        ele.answerAssets.forEach((ansAsset) => {
                                            if (ansAsset.assetId === settled.assetId) {
                                                Object.assign(ansAsset, settled);
                                            }
                                        });
                                    }
                                });
                            });
                            const next = new Map(prev);
                            next.set(submissionId, updated);
                            return next;
                        });
                    }

                    // Continue polling if assets remain
                    if (stillUploading.length && retryCount < WORKSHEET_ASSET_MAX_POLL_RETRIES) {
                        pollWorksheetAnswerAssets(submissionId, stillUploading, retryCount + 1);
                    }
                } catch (e) {
                    const message = e instanceof Error ? e.message : 'Failed to retrieve asset';
                    logger.warn(`Failed to poll worksheet answer assets [${submissionId}]`, {
                        submissionId,
                        error: e,
                        errorMessage: message,
                    });
                }
            }, WORKSHEET_ASSET_POLL_INTERVAL_MS);
        },
        []
    );

    /**
     * Clear the worksheet answer asset polling timeout. Should be called
     * on component unmount to prevent memory leaks.
     */
    const clearWorksheetAssetPolling = useCallback(() => {
        if (worksheetAssetPollRef.current) {
            clearTimeout(worksheetAssetPollRef.current);
        }
    }, []);

    /**
     * Fetch a worksheet form definition by ID and update local state.
     * Used when starting a new worksheet submission (no existing submission).
     *
     * @param worksheetId - the ID of the worksheet form to fetch
     * @returns the fetched form data, or undefined if the fetch failed
     */
    const handleFetchWorksheetForm = useCallback(
        async (worksheetId: string): Promise<WorksheetFormEntity | undefined> => {
            setWorksheetSubmissionFetching(true);
            setActiveSubmissionId(null);
            try {
                const result = await $fetchWorksheetForm({ worksheetFormId: worksheetId });
                setWorksheetFormData(result);
                return result;
            } catch (error) {
                logger.error(`Failed to fetch worksheet form [${worksheetId}]`, { worksheetId, error });
                return undefined;
            } finally {
                setWorksheetSubmissionFetching(false);
            }
        },
        []
    );

    /**
     * Retrieve an existing worksheet submission for editing. Returns the
     * cached data from the submission map if available; otherwise fetches
     * from the API and caches the result. Kicks off asset polling for any
     * assets still uploading.
     *
     * @param fetchCaseNumber - the case number the submission belongs to
     * @param submissionId - the unique ID of the submission
     * @returns the submission form data, or undefined if the fetch failed
     */
    const handleFetchWorksheetSubmission = useCallback(
        async (fetchCaseNumber: string, submissionId: string): Promise<WorksheetFormEntity | undefined> => {
            // Use cached data if available
            const cached = worksheetSubmissionMap.get(submissionId);
            if (cached) {
                setActiveSubmissionId(submissionId);
                return cached;
            }

            // Fetch from the API
            setWorksheetSubmissionFetching(true);
            setActiveSubmissionId(submissionId);
            try {
                const result = await $fetchWorksheetSubmission({ caseNumber: fetchCaseNumber, submissionId });

                // Cache the result in the submission map
                if (result) {
                    setWorksheetSubmissionMap((prev) => {
                        const next = new Map(prev);
                        next.set(submissionId, result);
                        return next;
                    });
                }

                // Check for uploading assets and start polling
                if (result?.elements) {
                    const uploadingAssets = result.elements
                        .flatMap((ele) => ele.answerAssets ?? [])
                        .filter((a) => a.assetProcessingStateId === AssetProcessingStateId.UPLOADING);

                    if (uploadingAssets.length) {
                        pollWorksheetAnswerAssets(submissionId, uploadingAssets, 0);
                    }
                }

                return result;
            } catch (error) {
                logger.error(`Failed to fetch worksheet submission [${submissionId}]`, { submissionId, error });
                return undefined;
            } finally {
                setWorksheetSubmissionFetching(false);
            }
        },
        [pollWorksheetAnswerAssets, worksheetSubmissionMap]
    );

    /**
     * Fetch available worksheet titles filtered by pilot user status
     * and update local state.
     *
     * @param fetchParams - the filter criteria and request options
     * @returns the worksheet titles array, or undefined if the fetch failed
     */
    const handleFetchWorksheetTitles = useCallback(
        async (fetchParams: FetchTitlesParams): Promise<WorksheetCaseGroupEntity[] | undefined> => {
            try {
                const result = await $fetchWorksheetTitles(fetchParams);
                setWorksheetTitleData(result);
                return result;
            } catch (error) {
                logger.error('Failed to fetch worksheet titles', { error });
                return undefined;
            }
        },
        []
    );

    /**
     * Fetch available worksheet groups from the API and update local state.
     * Used by the draft case stepper to eagerly load groups on mount.
     */
    const handleFetchAvailableWorksheetGroups = useCallback(async () => {
        // Reset state before fetching
        setIsLoadingAvailableGroups(true);
        setAvailableGroupError('');

        try {
            // Fetch worksheet titles for non-pilot users
            const result = await $fetchWorksheetTitles({ isPilotUser: false });

            // Set the fetched groups directly (entity shape matches WorksheetGroup)
            setAvailableWorksheetGroupData(result ?? []);
        } catch {
            setAvailableGroupError('Failed to load worksheet groups. Please try again.');
        } finally {
            setIsLoadingAvailableGroups(false);
        }
    }, []);

    // Effects

    // Auto-fetch available worksheet groups when the case number is available
    useEffect(() => {
        if (caseNumber) {
            handleFetchAvailableWorksheetGroups();
        }
    }, [caseNumber, handleFetchAvailableWorksheetGroups]);

    /**
     * Bulk-fetch all worksheet submissions when worksheetGroupData provides
     * submission IDs that have not yet been fetched. Uses a ref to track
     * which IDs have been requested, preventing redundant fetches.
     */
    useEffect(() => {
        if (!caseNumber || !worksheetGroupData) {
            return;
        }

        // Case has no worksheet groups; nothing to fetch
        if (worksheetGroupData.length === 0) {
            setHasLoadedInitialSubmissions(true);
            return;
        }

        // Collect submission IDs from case-level worksheet group data
        const allSubmissionIds: string[] = [];
        for (const group of worksheetGroupData) {
            for (const title of group.worksheetSubmissionTitles) {
                if (title.submissionId) {
                    allSubmissionIds.push(title.submissionId);
                }
            }
        }

        // Filter to only IDs we haven't fetched yet
        const submissionIds = allSubmissionIds.filter((id) => !bulkFetchedSubmissionIdsRef.current.has(id));

        if (submissionIds.length === 0) {
            setHasLoadedInitialSubmissions(true);
            return;
        }

        // Mark as in-flight to prevent duplicate fetches
        for (const id of submissionIds) {
            bulkFetchedSubmissionIdsRef.current.add(id);
        }

        // Fetch all submissions in parallel
        setIsLoadingSubmissions(true);
        $fetchAllWorksheetSubmissions({ caseNumber, submissionIds })
            .then((resultMap) => {
                setWorksheetSubmissionMap((prev) => {
                    const next = new Map(prev);
                    for (const [id, data] of resultMap) {
                        next.set(id, data);
                    }
                    return next;
                });
            })
            .catch((error) => {
                logger.error(`Failed to bulk-fetch worksheet submissions [${caseNumber}]`, { caseNumber, error });

                // Clear in-flight markers so a retry is possible
                for (const id of submissionIds) {
                    bulkFetchedSubmissionIdsRef.current.delete(id);
                }
            })
            .finally(() => {
                setIsLoadingSubmissions(false);
                setHasLoadedInitialSubmissions(true);
            });
    }, [caseNumber, worksheetGroupData]);

    /**
     * Submit worksheet answers (draft or final) and return the submission result.
     * Throws on failure; callers should handle errors.
     *
     * @param body - the worksheet answer payload
     * @returns the submission entity and server-assigned answer source IDs
     */
    const handleSubmitWorksheetAnswers = useCallback(async (body: SubmitAnswersBody): Promise<SubmitAnswersResult> => {
        return $submitWorksheetAnswers({ body });
    }, []);

    /**
     * Submit worksheet answers and upload any associated file assets.
     * Combines the answer submission and asset upload into a single
     * operation. Returns the persisted submission entity. Throws on
     * failure; if the submission throws, asset upload is skipped.
     *
     * @param submitParams - the submission parameters
     * @returns the persisted worksheet submission entity
     */
    const handleSubmitWorksheetWithAssets = useCallback(
        async (submitParams: {
            /** The case number the worksheet submission belongs to. */
            caseNumber: string;

            /** The current techline case status code. */
            caseStatus: string;

            /** Map of element IDs to their answer values, including file attachments. */
            formElementAnswer: Record<string, WorksheetElementAnswers>;

            /** Whether the submission is a draft save or final submission. */
            status: WorksheetAnswerStatus;

            /** The unique ID of the worksheet submission. */
            submissionId: string;

            /** The username of the user submitting answers. */
            userName: string;

            /** The worksheet definition ID. */
            worksheetId: string;
        }): Promise<WorksheetSubmissionEntity> => {
            // Build the answer submission body
            const body: SubmitAnswersBody = {
                answers: flatMap(submitParams.formElementAnswer).map((ans) => ({
                    elementId: ans.elementId,
                    selectedOptions: ans.selectedOptions,
                    worksheetGridCellAnswers: ans.worksheetGridCellAnswers,
                })),
                caseNumber: submitParams.caseNumber,
                status: submitParams.status,
                submissionId: submitParams.submissionId,
                userName: submitParams.userName,
                worksheetId: submitParams.worksheetId,
            };

            // Submit the answers (throws on failure; asset upload is skipped if this throws)
            const { answerSourceIds, entity } = await $submitWorksheetAnswers({ body });

            // Upload answer assets using server-assigned sourceIds from the submission response
            await uploadAnswerAssetsAfterSubmission({
                answerSourceIds,
                caseNumber: submitParams.caseNumber,
                caseStatus: submitParams.caseStatus,
                formElementAnswer: submitParams.formElementAnswer,
            });

            return entity;
        },
        []
    );

    /**
     * Create answer asset records and upload the corresponding files
     * to presigned URLs. Returns an empty array on failure to prevent
     * crashes in upstream callers.
     *
     * @param createParams - the asset metadata, files, and case status
     * @returns the created answer asset entities, or an empty array if creation failed
     */
    const handleCreateAndUploadAnswerAssets = useCallback(
        async (createParams: CreateAndUploadAnswerAssetsParams): Promise<AnswerAssetEntity[]> => {
            try {
                return await $createAndUploadAnswerAssets(createParams);
            } catch (error) {
                logger.error('Failed to create and upload answer assets', { error });
                return [];
            }
        },
        []
    );

    /**
     * Hide worksheet uploaded files from TechShare by setting displayInTechShare to false.
     * This prevents the specified assets from being visible in TechShare while keeping
     * them available in the Techline case.
     *
     * @param assetIds - the IDs of the assets to hide from TechShare
     * @returns true if the operation succeeded, false otherwise
     */
    const handleHideWorksheetFilesFromTechShare = useCallback(async (assetIds: string[]): Promise<boolean> => {
        try {
            await $hideFromTechShare({ assetIds });
            return true;
        } catch (error) {
            logger.error('Failed to hide worksheet files from TechShare', { assetIds, error });
            return false;
        }
    }, []);

    /**
     * Re-fetch worksheet submission groups and all individual submissions from
     * the API in a single awaitable call. Updates the in-memory submission map
     * and bulk-fetch tracker before notifying the parent of new group data, so
     * React batches both state updates into one render with complete data.
     */
    const refreshWorksheetData = useCallback(async () => {
        if (!caseNumber) {
            return;
        }
        setIsLoadingWorksheets(true);
        try {
            // Fetch groups and all submissions atomically via the composite operation
            const { groups, submissionMap } = await $refreshWorksheetSubmissions({ caseNumber });

            // Update the submission map with fresh data
            if (submissionMap.size > 0) {
                setWorksheetSubmissionMap((prev) => {
                    const next = new Map(prev);
                    for (const [id, data] of submissionMap) {
                        next.set(id, data);
                    }
                    return next;
                });
            }

            // Mark all fetched IDs so the bulk-fetch effect doesn't re-fetch them
            bulkFetchedSubmissionIdsRef.current.clear();
            for (const id of submissionMap.keys()) {
                bulkFetchedSubmissionIdsRef.current.add(id);
            }

            // Notify parent of new group data (triggers enrichment and re-merge)
            if (groups) {
                onWorksheetGroupDataChange(groups);
            }
        } catch (error) {
            logger.error(`Failed to refresh worksheet data [${caseNumber}]`, { caseNumber, error });
        } finally {
            setIsLoadingWorksheets(false);
        }
    }, [caseNumber, onWorksheetGroupDataChange]);

    /**
     * Delete a draft worksheet submission, then refresh worksheet groups
     * so the parent's worksheetGroupData stays in sync. Also removes the
     * submission from the cached map.
     *
     * @param submissionId - the ID of the draft submission to delete
     * @returns true if deletion succeeded, false otherwise
     */
    const handleDeleteDraftWorksheetSubmission = useCallback(
        async (submissionId: string): Promise<boolean> => {
            try {
                const success = await $deleteDraftWorksheetSubmission({ submissionId });
                if (success) {
                    // Remove from the cached map
                    setWorksheetSubmissionMap((prev) => {
                        const next = new Map(prev);
                        next.delete(submissionId);
                        return next;
                    });
                    bulkFetchedSubmissionIdsRef.current.delete(submissionId);

                    if (caseNumber) {
                        await refreshWorksheetData();
                    }
                }
                googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetDeleteDraft, {
                    success,
                });
                return success;
            } catch (error) {
                googleAnalyticsService.track(GoogleAnalyticsEvents.WorksheetDeleteDraft, {
                    success: false,
                });
                logger.error(`Failed to delete draft worksheet submission [${submissionId}]`, { submissionId, error });
                return false;
            }
        },
        [caseNumber, refreshWorksheetData]
    );

    return {
        /** Error message from loading available worksheet groups. */
        availableGroupError,

        /** Available worksheet groups with metadata (titles not yet loaded per-group). */
        availableWorksheetGroupData,

        /** Clear the worksheet answer asset polling timeout (call on unmount). */
        clearWorksheetAssetPolling,

        /** Worksheet groups enriched with submission metadata and validation results. */
        enrichedWorksheetGroupData,

        /** Create answer asset records and upload files to presigned URLs. */
        handleCreateAndUploadAnswerAssets,

        /** Delete a draft worksheet submission and refresh worksheet groups. */
        handleDeleteDraftWorksheetSubmission,

        /** Hide worksheet uploaded files from TechShare display. */
        handleHideWorksheetFilesFromTechShare,

        /** Whether the initial bulk-fetch of worksheet submissions has completed (or was skipped). */
        hasLoadedInitialSubmissions,

        /** Fetch available worksheet groups from the API. */
        handleFetchAvailableWorksheetGroups,

        /** Fetch a worksheet form definition by ID. */
        handleFetchWorksheetForm,

        /** Fetch an existing worksheet submission by case number and submission ID. */
        handleFetchWorksheetSubmission,

        /** Fetch available worksheet titles filtered by pilot user status. */
        handleFetchWorksheetTitles,

        /** Submit worksheet answers (draft or final). */
        handleSubmitWorksheetAnswers,

        /** Submit worksheet answers and upload any associated file assets in one operation. */
        handleSubmitWorksheetWithAssets,

        /** Whether available worksheet groups are currently being fetched. */
        isLoadingAvailableGroups,

        /** Whether all worksheet submissions are being bulk-fetched. */
        isLoadingSubmissions,

        /** Whether worksheet data is currently being loaded. */
        isLoadingWorksheets,

        /** Re-fetch worksheet submission groups and notify the parent. */
        refreshWorksheetData,

        /** Directly set the worksheet loading state. */
        setIsLoadingWorksheets,

        /** The fetched worksheet form definition. */
        worksheetFormData,

        /** The active worksheet submission data for the currently open editor. */
        worksheetSubmissionData,

        /** Whether a worksheet submission or form is currently being fetched. */
        worksheetSubmissionFetching,

        /** All cached worksheet submissions keyed by submission ID. */
        worksheetSubmissionMap,

        /** The fetched worksheet title groups. */
        worksheetTitleData,

        /** Validation results for draft worksheets. Keyed by submissionId; true if valid. */
        worksheetValidationResults,
    };
}

/**
 * Create and upload answer assets after a successful worksheet answer submission.
 * Extracts file attachments from form element answers, creates asset records,
 * and uploads files to presigned URLs. Uses server-assigned source IDs from the
 * submission response to link assets to their answers.
 *
 * @param params - the context for asset creation
 */
async function uploadAnswerAssetsAfterSubmission(params: {
    /** Map of element IDs to server-assigned source IDs from the submission response. */
    answerSourceIds: Record<string, string>;

    /** The case number the assets belong to. */
    caseNumber: string;

    /** The current techline case status code, used for asset upload authorization. */
    caseStatus: string;

    /** Map of element IDs to their answer values, including file attachments to upload. */
    formElementAnswer: Record<string, WorksheetElementAnswers>;
}): Promise<void> {
    const { answerSourceIds, caseNumber, caseStatus, formElementAnswer } = params;

    try {
        const { assets, elementIds, sourceIdFileMap } = buildAnswerAssetBodies(
            formElementAnswer,
            caseNumber,
            caseStatus,
            answerSourceIds
        );

        if (!assets.length) {
            return;
        }

        // Upload assets and update the form element answers with the returned asset IDs
        const uploadedAssetEntities = await $createAndUploadAnswerAssets({
            assets,
            files: assets.map((a) => sourceIdFileMap[a.worksheetQuestionSubmissionSourceId]),
            techlineCaseStatus: caseStatus,
        });

        // Update form element answers with the server-assigned asset IDs
        const uploadedAssetIds: string[] = [];
        uploadedAssetEntities.forEach((uploadedAssetEntity, idx) => {
            const elementId = elementIds[idx];
            const answerFile = formElementAnswer[elementId]?.asset?.answerFile;
            if (answerFile) {
                answerFile.assetId = uploadedAssetEntity.assetId;
            }
            uploadedAssetIds.push(uploadedAssetEntity.assetId);
        });

        // Wait for all uploaded assets to finish server-side processing
        // (thumbnail generation, media entry creation, etc.)
        await $waitForAssetProcessing({ assetIds: uploadedAssetIds });
    } catch (error) {
        logger.error(`Failed to upload answer assets [${caseNumber}]`, { caseNumber, error });
    }
}
